/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { observer } from "mobx-react-lite";
import { InputForm } from "../../../Forms/InputForm";
import { OptionalForm } from "../../../Forms/OptionalForm";
import { useRootStore } from "../../../../../stores/hooks";
import type { OptionalFields } from "..";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../../types/candidate";
import type { Display_DisplayOptional } from "@smartsuite/automation-engine-api";
import { RecordsToFind } from "../../../Forms/InputForm/FieldEditor/FieldEditorRow/fields/LimitSortEditor/RecordsToFind";
import { FIND_RECORD_LIMIT_ID } from "../../../../../utils/constants";
import { shouldShowActionInputValidationErrors } from "../../../../../utils/action-helpers";
import { Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { ActionCredentialSelector } from "../ActionCredentialSelection";
import type { AuthAttempt } from "../../../../../auth";
import { useFindActionAuthenticationIds } from "../../find-action-authentication-ids";
import { useUpstreamActions } from "../../../Forms/InputForm/hooks/use-upstream-actions";

export const AlwaysFieldList = observer<{
  loading: boolean;
  action: ActionCandidate;
  onInputChange: (input: InputCandidate, action: ActionCandidate) => void;
  afterConditions: boolean;
  afterRecordListInput: boolean;
  onRemove: (id: string) => void;
  setAuthAttempt: (auth: AuthAttempt) => void;
}>(
  ({
    loading,
    action,
    onInputChange,
    afterConditions,
    afterRecordListInput,
    onRemove,
    setAuthAttempt,
  }) => {
    const store = useRootStore();
    const { candidate } = store.automations;
    // Only actions upstream of this one on its own path (no sibling-branch
    // outputs) are offered as data sources -- spec 8.1/8.2.
    const scopedActions = useUpstreamActions(candidate, action);
    const [placeAfterId, setPlaceAfterId] = useState<string | undefined>(
      undefined,
    );
    const findActionAuthenticationIds = useFindActionAuthenticationIds();

    const integration = store.integrations.list.find(
      (integration) =>
        integration.integrationId === action.actionReference?.integrationId,
    );

    const alwaysFields = findAlwaysFields(
      action,
      afterConditions,
      afterRecordListInput,
    );
    const optionalFields = findOptionalFields(action);

    const findRecordsLimitInput = alwaysFields.find(
      (field) => field.inputId === FIND_RECORD_LIMIT_ID,
    );

    useEffect(() => {
      const integration = store.integrations.list.find(
        (item) => item.integrationId === action.actionReference?.integrationId,
      );

      if (
        integration !== undefined &&
        integration.authenticationMethods.length > 0
      ) {
        setPlaceAfterId(integration.authenticationMethods[0].placeAfterId);
      }
    }, []);

    const getOptionalFieldsForInput = (
      inputId: string,
    ): OptionalFields | undefined =>
      optionalFields.find((item) => item.linkedToInputId === inputId);

    const needToShowError = (hash: string): boolean => {
      if (hash !== action.hash) {
        return candidate.isActionSubmitted(hash);
      }

      return shouldShowActionInputValidationErrors(
        candidate,
        action,
        store.automations.isAllDynamicDescriptionsLoaded(),
      );
    };

    return (
      <>
        {findRecordsLimitInput !== undefined && (
          <RecordsToFind inputs={alwaysFields} action={action} />
        )}
        {alwaysFields.map((input, index) => {
          const optionalGroup = getOptionalFieldsForInput(input.inputId);
          return (
            <Fragment key={`${action.hash}-${input.inputId}`}>
              <InputForm
                loading={loading}
                key={`${action.hash}-${input.inputId}`}
                input={input}
                nextInput={
                  alwaysFields.length > index + 1
                    ? alwaysFields[index + 1]
                    : undefined
                }
                descriptions={action.description.inputs}
                trigger={candidate.self.trigger}
                actions={scopedActions}
                actionHash={action.hash}
                currentAction={action}
                integrations={store.integrations.list}
                showValidationError={needToShowError(action.hash)}
                onChange={(input) => {
                  onInputChange(input, action);
                }}
              />
              {optionalGroup && (
                <OptionalForm
                  key={`optional-${action.hash}`}
                  optionalFields={optionalGroup}
                  loading={loading}
                  descriptions={action.description.inputs}
                  trigger={candidate.self.trigger}
                  actions={scopedActions}
                  actionHash={action.hash}
                  currentAction={action}
                  integrations={store.integrations.list}
                  showValidationError={needToShowError(action.hash)}
                  onChange={(input) => {
                    onInputChange(input, action);
                  }}
                />
              )}

              {placeAfterId === input.inputId &&
                findActionAuthenticationIds(action).length > 0 && (
                  <ActionCredentialSelector
                    action={action}
                    needToShowError={needToShowError}
                    setAuthAttempt={setAuthAttempt}
                    onRemove={onRemove}
                    descriptions={action.description.inputs}
                    authenticationMethodDescription={
                      integration !== undefined &&
                      integration.authenticationMethods.length > 0
                        ? integration.authenticationMethods[0]
                        : undefined
                    }
                    nextInput={
                      alwaysFields.length > index + 1
                        ? alwaysFields[index + 1]
                        : undefined
                    }
                  ></ActionCredentialSelector>
                )}
            </Fragment>
          );
        })}
      </>
    );
  },
);
AlwaysFieldList.displayName = "AlwaysFieldList";

// TODO refactor these fns
const findAlwaysFields = (
  action: ActionCandidate,
  afterConditions: boolean,
  afterRecordListInput: boolean,
): InputCandidate[] => {
  const descriptionIds = action.description.inputs.map((i) => {
    return i.inputId;
  });

  return action.inputs
    .filter((input) => {
      const inputDescription = action.description.inputs.find(
        (i) => i.inputId === input.inputId,
      );
      if (afterRecordListInput) {
        return inputDescription?.display?.displayAfterRecordListInput === true;
      }
      return inputDescription?.display?.displayAfterRecordListInput !== true;
    })
    .filter((input) => {
      const findDescription = action.description.inputs.find(
        (description) => description.inputId === input.inputId,
      );

      if (findDescription !== undefined) {
        if (
          afterConditions &&
          findDescription.display?.displayAfterConditions !== true
        ) {
          return false;
        }
        if (
          !afterConditions &&
          findDescription.display?.displayAfterConditions === true
        ) {
          return false;
        }
        return findDescription.display?.display.oneofKind === "always";
      }
      return false;
    })
    .sort((a, b) => {
      return (
        descriptionIds.indexOf(a.inputId) - descriptionIds.indexOf(b.inputId)
      );
    });
};

const findOptionalFields = (action: ActionCandidate): OptionalFields[] => {
  const optionalDescriptions: Display_DisplayOptional[] =
    action.description.inputs
      .filter((item) => item.display?.display.oneofKind === "optional")
      .map(
        (i) =>
          i.display?.display.oneofKind === "optional" &&
          i.display.display.optional,
      )
      .filter((i): i is Display_DisplayOptional => Boolean(i));

  const uniqGroups = [
    ...new Map(
      optionalDescriptions.map((item) => [item.groupId, item]),
    ).values(),
  ];

  return uniqGroups.map((optionalDescription): OptionalFields => {
    const inputs = action.inputs.filter((input) => {
      const findDescription = action.description.inputs.find(
        (description) => description.inputId === input.inputId,
      );

      if (findDescription !== undefined) {
        return (
          findDescription.display?.display.oneofKind === "optional" &&
          findDescription.display?.display.optional.groupId ===
            optionalDescription.groupId
        );
      }
      return false;
    });

    return {
      groupId: optionalDescription.groupId,
      label: optionalDescription.label,
      elapsedLabel: optionalDescription.elapsedLabel,
      linkedToInputId: optionalDescription.linkedToInputId,
      inputs,
    };
  });
};
